import React, { useId, useRef, useState, useEffect } from 'react';
import { formatDate } from '../../utils/dateFormat';
import { FIELD_LABEL_CLASS } from '../../constants/inputs';
import CurrencyInput from '../ui/CurrencyInput';
import { toPayloadAmount, isMalformedAmount } from '../../utils/moneyPayload';
import { toMoneyNumber, roundMoney } from '../../utils/money';
import {
  hireOverrunDelta,
  normaliseHireRateType,
  calendarDayGap,
  billableHireDays,
  hireLineCost,
} from '../../utils/hireCost';
import { EM_DASH } from '../../utils/moneyOrAbsent';
import {
  ADJUSTMENT_EXISTS_TITLE,
  ADJUSTMENT_KEEP_LABEL,
  ADJUSTMENT_REMOVE_LABEL,
  adjustmentRemoveCopy,
} from './HireAdjustmentDecisionDialog';

/**
 * THE OFF-HIRE PRICE CHOICE — ONE per-line component shared by the single-line
 * Mark Off Hired dialog (`HireActionModal.js`) and `OffHireAllDialog.js`, plus
 * every pure price rule both dialogs use. Never copy it into either dialog.
 *
 * ⚖️ Owner ruling 2026-09-28, on the Off hire all dialog: *"when we try to off
 * hire something it should show the original estimated cost, and the current
 * off hire price if early or late and then the user can decide if they use
 * the original cost, current cost or adjust"*. Default: *"keep original price
 * as the default selected but make sure the other two options are
 * available"*. So three radios per line:
 *   · Keep original  — DEFAULT, always; sends NO adjustment. This is the
 *     2026-09-01 rule OFF-HIRE NEVER AUTO-REPRICES, unchanged.
 *   · Use actual     — the line's cost over hire_from → return date (inclusive
 *     days, weekly = ceil(days / 7), quantity multiplies — `hireOverrunDelta`
 *     over the shared `hireCost.js`, never re-derived). An EXPLICIT,
 *     attributed override sent through the existing final-price path with an
 *     auto note ("Priced to actual dates: …").
 *   · Set final price — the existing "Adjust final price" path, unchanged.
 * 🔀 RENAMED 2026-10-19 (U2): "Keep booked price" / "Charge actual hire" /
 * "Set final price". 🔀 2026-10-20 (owner, from a live mock-up: *"would it be
 * simplier to alwasy show the over ride price section?"* → *"Build the off-hire
 * changes as mocked."*): the options are ALWAYS shown — early, on time and late.
 * The 10-19 collapse and its Override / Cancel override link are DELETED
 * (pre-deletion SHA `aafb4ba1`). Keep stays the default and sends nothing.
 *
 * 💷 What "Use actual" SENDS (`current total + (actual − original)`, never the
 * bare actual) → `client/src/components/hires/CLAUDE.md` (moved 2026-10-21).
 *
 * 🪤 The "actual" DISPLAY is `line_total + delta`, which equals
 * `hireLineCost` over the actual span for any line whose stored `line_total`
 * is consistent with its rates (pinned by `OffHirePriceChoice.test.js` §1). On
 * a legacy line whose stored total disagrees with its rates, the DIFFERENCE is
 * still exactly the date's cost change — the stored price is not re-derived.
 *
 * No server change: the choice is recorded by the note, which lands on the
 * adjustment row and in the `hire_adjustment_added` audit line with the name.
 *
 * ⚖️ Keep or remove an earlier adjustment (owner ruling 2026-10-06, 6B) →
 * `client/src/components/hires/CLAUDE.md` (moved 2026-10-21).
 */

/**
 * The cost change of returning on `pickedDate` (the RETURN date being
 * considered, not necessarily `item.hire_to`): `{ delta }`, signed, or `null`
 * when the span is unknowable (no `hire_from`/`hire_to`) — never a false £0.
 * Read by the prefill and by "Charge actual hire"; never persisted.
 *
 * 🔑 Reuses `hireOverrunDelta`'s WHOLE-SPAN arithmetic rather than pricing
 * the overrun gap in isolation — pricing the gap alone double-charges a
 * weekly line that is still inside its already-paid week (Mon→Wed billed a
 * full week; returning Fri costs £0 extra, not a phantom second week).
 *
 * 🔻 2026-10-20 (owner R2 + review fix M1): the sentence this used to build
 * ("Estimated extra cost …", "falls within the week already paid for", the
 * weekly-only gate on that sentence from the 2026-09-02 Task 9 sweep) is
 * DELETED — the line that rendered it went with R2, so it rendered nowhere.
 * Pre-deletion SHA `11060ca6`. The re-export from `HireActionModal.js` went too.
 */
export function describeOffHireEstimate(item, pickedDate) {
  const delta = hireOverrunDelta(item, pickedDate);
  return delta == null ? null : { delta };
}

/**
 * The final-price section's preview: what the SERVER will do with the typed
 * final price. Mirrors `PATCH /off-hire`'s rule — `current_total =
 * roundMoney(line_total + adjustment_total)`, `offset = roundMoney(final −
 * current_total)` — from the row the list already carries. `currentTotal` is
 * `null` when the row has no usable `line_total` (an older cached row), and
 * `offset` is `null` whenever either side is unknown; the copy then says the
 * server will work it out rather than guessing. 🪤 `adjustment_total` is
 * `NULL` when no prior rows exist and a real signed SUM otherwise — `0` is a
 * value (two offsetting rows), so it is coerced, never falsy-tested.
 */
export function describeFinalPricePreview(item, finalPriceRaw, formatMoney) {
  const agreed = toMoneyNumber(item.line_total);
  const prior = toMoneyNumber(item.adjustment_total);
  const currentTotal = agreed === null ? null : roundMoney(agreed + (prior === null ? 0 : prior));
  const trimmed = String(finalPriceRaw ?? '').trim();
  const final = trimmed === '' || isMalformedAmount(finalPriceRaw) ? null : toPayloadAmount(finalPriceRaw, null);
  const offset = final === null || currentTotal === null ? null : roundMoney(final - currentTotal);
  // 🔀 2026-10-20 (R4): the WORDS moved to `describeChoiceSummary`, the one
  // summary under the options; this answers the numbers alone.
  return { currentTotal, final, offset };
}

/**
 * ⚖️ THE PREFILL (owner ruling 2026-09-28, replacing the 09-27 "current cost"):
 * the box opens holding *the cost at the RETURN DATE the user has picked, plus
 * the hire's earlier adjustments* — i.e. what this return would settle at if
 * the extra or fewer days were charged at the agreed rate and nothing already
 * on the order were undone. As a string for the input; `''` when unknowable.
 *
 * 🔑 ONE CALCULATION, NOT TWO. The date half is EXACTLY the figure the
 * "Charge actual hire" row prices for that date (`describeOffHireEstimate`
 * → `hireOverrunDelta`, whole-span, signed), applied to the same base the
 * server's offset rule uses (`describeFinalPricePreview`'s `currentTotal` =
 * `line_total + adjustment_total`). So prefill = current_total + delta, and
 * the consequences the ruling pins fall out rather than being special-cased:
 *   · on-plan return (delta 0) with a prior +£200 row → prefill = current
 *     total → offset 0 → NO row, note optional — never a refund of the £200;
 *   · overdue item returned per the paperwork 3 days ago → the 3 days' cost
 *     on top of the agreed price, plus any priors.
 * 🪤 `item.current_cost` is NOT the base: it is rate × days to TODAY and
 * EXCLUDES prior adjustments, so an untouched prefill on an on-plan hire with
 * a prior row minted a row refunding that prior. That was the 09-27 shape.
 * 🪤 A `null` delta (no usable dates) prefills the current total alone; a
 * `null` current total (no `line_total` on the row) prefills BLANK — blank is
 * refused on submit, where a guessed `£0.00` would be a real price.
 */
export function prefillFinalPrice(item, returnDate, formatMoney) {
  const { currentTotal } = describeFinalPricePreview(item, '', formatMoney);
  if (currentTotal === null) return '';
  const estimate = describeOffHireEstimate(item, returnDate);
  const delta = estimate ? estimate.delta : null;
  const value = roundMoney(currentTotal + (delta === null ? 0 : delta));
  return value === null ? '' : String(value);
}

/**
 * ⚖️ THE FOLLOW RULE (owner 2026-09-28): the box follows the return date until
 * the user has typed a figure of their own — and a box they have CLEARED
 * follows again (ruling *"ok go for it"*, reviewer finding L1 on
 * `feat/offhire-final-price`: a cleared box used to stay blank for good).
 * Answers "is the box now the user's own figure?" for one input value.
 * Shared with `OffHireAllDialog.js` — one rule, never a copy.
 */
export function finalPriceTouchedAfterInput(value) {
  return String(value ?? '').trim() !== '';
}

/**
 * THE submit-time screen on an opened final-price section — malformed, blank,
 * negative, and the note required whenever the previewed offset is not exactly
 * zero. Returns `{ error }` (the inline message) or `{ adjustment }` (the
 * payload's `{ final_price, note? }`). Shared with `OffHireAllDialog.js`, per
 * line, so the two dialogs refuse the same inputs with the same words.
 */
export function buildFinalPriceAdjustment(item, finalPrice, adjustNote, formatMoney) {
  // 💷 `toPayloadAmount`, never a bare `Number` (2026-09-22): the shared
  // builder rounds to 2dp and answers `null` for malformed text, by a rule
  // that also catches `'1,000'` (`Number` says `NaN`; `parseFloat` would
  // have said 1). Malformed and BLANK are refused BY NAME here rather than
  // silently off-hiring without the price the user opened the section to
  // set (the 2026-09-23 silent-drop defect, in its final-price form). The
  // server refuses both too; this only makes the refusal reachable inline.
  if (isMalformedAmount(finalPrice)) {
    return { error: 'Final price must be a plain number — for example 1000, not 1,000.' };
  }
  if (String(finalPrice).trim() === '') {
    return { error: 'Enter the final price for this return, or choose "Keep booked price" to leave the price as it is.' };
  }
  // 🚨 THE SIGN IS DECIDED ON THE RAW PARSED VALUE, NOT ON THE ROUNDED ONE
  // (the 2026-09-23 lesson, in its final-price form): `-0.004` rounds to
  // `-0`, and `-0 < 0` is false, so a check on the built payload would wave
  // a typed minus through as a £0.00 price. `toMoneyNumber` on the trimmed
  // text is what the user actually typed.
  if (toMoneyNumber(String(finalPrice).trim()) < 0) {
    return { error: 'A final price cannot be negative — enter the total this return settles at.' };
  }
  const preview = describeFinalPricePreview(item, finalPrice, formatMoney);
  const note = String(adjustNote ?? '').trim();
  // The note is required whenever a row WILL (or might) result: the
  // preview's offset is non-zero, or unknown because the row carries no
  // usable line_total. Only a preview of exactly zero waives it — and the
  // server re-derives from fresh rows and refuses without a note if its
  // offset turns out non-zero, so a stale preview cannot mint an unnoted row.
  // 🔀 2026-10-19 (U2): NO error message any more — the note box and its label
  // are already RED while a note is owed (`finalPriceNoteOwed`), and Confirm
  // answers `noteOwed` so the dialog nudges the box (shake, flash, focus)
  // instead of submitting. The server's own refusal stays.
  if (preview.offset !== 0 && !note) {
    return { noteOwed: true };
  }
  return { adjustment: note ? { final_price: preview.final, note } : { final_price: preview.final } };
}

// ─── the price choice ────────────────────────────────────────────────────────

export const PRICE_CHOICE_KEEP = 'keep';
export const PRICE_CHOICE_ACTUAL = 'actual';
export const PRICE_CHOICE_SET = 'set';

/** The earlier-adjustment decision under "Use actual" (6B). KEEP is the default. */
export const PRIOR_ADJUSTMENT_KEEP = 'keep';
export const PRIOR_ADJUSTMENT_REMOVE = 'remove';

/** A line's price-choice state before the user touches anything. */
export function initialPriceChoiceState() {
  return { choice: PRICE_CHOICE_KEEP, price: '', touched: false, note: '', error: '', priorAdjustment: PRIOR_ADJUSTMENT_KEEP };
}

/** True only when "Use actual" with REMOVE is genuinely in force for this line. */
function removesPrior(actualOption, state) {
  return actualOption.asksPriorDecision && !!state && state.priorAdjustment === PRIOR_ADJUSTMENT_REMOVE;
}

/** "Use actual"'s auto note — names the keep/remove answer when one was asked (6B). */
function actualAutoNote(actualOption, state, formatMoney) {
  if (!actualOption.asksPriorDecision) return actualOption.note;
  const verb = removesPrior(actualOption, state) ? 'removed' : 'kept';
  return `${actualOption.note}; earlier adjustment of ${formatMoney(actualOption.prior)} ${verb}`;
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * Early / late / on time, counted as the EXCLUSIVE gap from the last hired
 * day (`hire_to`) to the return date — returning ON the last day is on time
 * (`calendarDayGap`, the overrun's own interval). `null` when unknowable.
 */
export function describeOffHireTiming(item, returnDate) {
  const gap = calendarDayGap(item && item.hire_to, returnDate);
  if (gap === null) return null;
  if (gap === 0) return { kind: 'on-time', days: 0, text: 'on time' };
  const days = Math.abs(gap);
  return gap > 0
    ? { kind: 'late', days, text: `${plural(days, 'day')} late` }
    : { kind: 'early', days, text: `${plural(days, 'day')} early` };
}

/** "1 week" / "2 weeks" for a weekly line, "10 days" otherwise; `null` when unknowable. */
function spanLabel(item, from, to) {
  const days = billableHireDays(from, to);
  if (days === null) return null;
  if (normaliseHireRateType(item.hire_rate_type) === 'weekly') return plural(Math.ceil(days / 7), 'week');
  return plural(days, 'day');
}

/**
 * The "Use actual" option for one line at one return date. Never throws.
 * `{ available: false, reason }` when it cannot be computed; otherwise
 * `{ original, actual, delta, matches, finalPrice, originalSpan, actualSpan, note }`
 * — `finalPrice` is what the request sends (see the module docblock).
 */
export function describeActualOption(item, returnDate, formatMoney) {
  const original = toMoneyNumber(item.line_total);
  const base = { available: false, original, originalSpan: spanLabel(item, item.hire_from, item.hire_to) };
  if (!item.hire_from || !item.hire_to || calendarDayGap(item.hire_from, item.hire_to) === null) {
    return { ...base, reason: 'This line has no hire dates, so its cost to the return date cannot be worked out.' };
  }
  if (original === null) {
    return { ...base, reason: 'This line has no stored price to compare against.' };
  }
  if (!returnDate || calendarDayGap(item.hire_from, returnDate) === null) {
    return { ...base, reason: 'Choose a return date to see the actual cost.' };
  }
  if (calendarDayGap(item.hire_from, returnDate) < 0) {
    return { ...base, reason: 'The return date is before this hire started.' };
  }
  const { delta } = describeOffHireEstimate(item, returnDate);
  const actual = roundMoney(original + delta);
  // 🔑 ONE CALCULATION (review fix round 1, 2026-09-28): the figure SENT is
  // derived from the SAME `actual` the row DISPLAYS, never from a second
  // `describeOffHireEstimate` call — two calls let a qty-dropping change to
  // one path show £2,424 and send £1,818 with every test green. Earlier
  // adjustments are added so the server's offset never refunds them.
  const prior = toMoneyNumber(item.adjustment_total);
  const finalPrice = roundMoney(actual + (prior === null ? 0 : prior));
  // 6B: only a REAL earlier adjustment asks keep/remove — `0` (two offsetting
  // rows) is a value but there is nothing to remove, so it asks nothing.
  const asksPriorDecision = prior !== null && prior !== 0;
  // Legacy debris: a stored price that disagrees with rate × days. The
  // DIFFERENCE is still the date's cost change; say so rather than re-derive.
  const storedDiffers = roundMoney(hireLineCost(item)) !== original;
  const days = billableHireDays(item.hire_from, returnDate);
  const weekly = normaliseHireRateType(item.hire_rate_type) === 'weekly';
  const note = `Priced to actual dates: ${formatDate(item.hire_from)} – ${formatDate(returnDate)} ` +
    `(${plural(days, 'day')}${weekly ? `, ${plural(Math.ceil(days / 7), 'week')}` : ''})`;
  return {
    available: true,
    original,
    actual,
    delta,
    // EXACTLY zero, never "close enough": a pence-sized late return is real money.
    matches: delta === 0,
    finalPrice,
    prior,
    asksPriorDecision,
    storedDiffers,
    originalSpan: base.originalSpan,
    actualSpan: spanLabel(item, item.hire_from, returnDate),
    note,
  };
}

/**
 * True while "Set final price" holds a figure that moves the price and no note
 * has been written — the label and the box go RED, and Confirm nudges the box
 * rather than submitting (owner 2026-10-19). Same predicate as the builder's.
 */
export function finalPriceNoteOwed(item, state, formatMoney) {
  if (!state || state.choice !== PRICE_CHOICE_SET) return false;
  const p = describeFinalPricePreview(item, state.price, formatMoney);
  return p.final !== null && p.offset !== 0 && !String(state.note ?? '').trim();
}

/**
 * THE submit-time builder for one line's choice. Returns `{ adjustment: null }`
 * (send NO adjustment — Keep original, or an actual that matches), `{
 * adjustment: { final_price, note? } }`, or `{ error }`. Shared by both dialogs.
 * 🔀 2026-10-20: the choice is honoured on EVERY return date — the options are
 * always shown, so there is no collapsed state to override it.
 */
export function buildOffHirePriceChoice(item, returnDate, state, formatMoney) {
  const choice = state && state.choice;
  if (choice === PRICE_CHOICE_SET) {
    return buildFinalPriceAdjustment(item, state.price, state.note, formatMoney);
  }
  if (choice === PRICE_CHOICE_ACTUAL) {
    const a = describeActualOption(item, returnDate, formatMoney);
    if (!a.available) return { error: a.reason };
    if (a.matches) return { adjustment: null };
    // 6B: REMOVE sends the bare actual (the server's offset then cancels the
    // earlier rows); KEEP sends actual + prior, exactly as before the ruling.
    const finalPrice = removesPrior(a, state) ? a.actual : a.finalPrice;
    const extra = String(state.note ?? '').trim();
    const base = actualAutoNote(a, state, formatMoney);
    const note = extra ? `${base} — ${extra}` : base;
    return buildFinalPriceAdjustment(item, String(finalPrice), note, formatMoney);
  }
  return { adjustment: null };
}

/**
 * The state after the RETURN DATE changes: an open, untouched Set-final-price
 * box follows the date (the 2026-09-28 follow rule). A "Use actual" choice the
 * new date makes unavailable, or equal to the original (its row is hidden),
 * falls back to KEEP ORIGINAL — so a hidden choice cannot resurface when the
 * date moves back, and a disabled radio can never stay checked.
 *
 * ⚖️ A BUCKET CROSSING RESETS (owner ruling A, 2026-10-19 — still binding after
 * the 2026-10-20 always-shown ruling): when the line moves between early / on
 * time / late (`describeOffHireTiming`'s `kind`; an unknowable timing is its own
 * bucket), the choice goes back to Keep booked price — no carry in EITHER
 * direction. A move WITHIN one bucket (early → a different early date) keeps the
 * choice under the rules above. `prevDate` is the date being left; omitted (the
 * dialog's OPENING call — the Gantt shorten route's "Charge actual"
 * preselection, owner 2026-10-05 option A) nothing is crossed, so nothing resets.
 */
export function priceChoiceAfterDateChange(item, state, nextDate, formatMoney, prevDate) {
  if (prevDate !== undefined) {
    const bucket = (d) => { const t = describeOffHireTiming(item, d); return t ? t.kind : 'unknown'; };
    if (bucket(prevDate) !== bucket(nextDate)) return { ...state, ...initialPriceChoiceState() };
  }
  if (state.choice === PRICE_CHOICE_ACTUAL) {
    const a = describeActualOption(item, nextDate, formatMoney);
    if (!a.available || a.matches) return { ...state, choice: PRICE_CHOICE_KEEP, note: '', error: '' };
    return state;
  }
  if (state.choice === PRICE_CHOICE_SET && !state.touched) {
    return { ...state, price: prefillFinalPrice(item, nextDate, formatMoney) };
  }
  return state;
}

/** A signed difference: `+£606.00` / `−£100.00` (U+2212, the house's signed-delta form). */
function signedMoney(n, formatMoney) {
  if (n === 0) return formatMoney(0);
  return `${n > 0 ? '+' : '−'}${formatMoney(Math.abs(n))}`;
}

/**
 * ⚖️ THE ONE SUMMARY under the options (owner 2026-10-20, R4 — it replaces the
 * live explainer AND the "Difference:" line): what the chosen option does to
 * this hire's price, before confirm. Returns `{ kind, text?, adjustment?,
 * adjustmentText?, newTotal? }`:
 *   · `none`    — Keep, or a change of exactly 0: ONE muted line, no rows;
 *   · `prompt`  — Set final price with an empty box;
 *   · `unknown` — no usable stored price, so only the server can work it out;
 *   · `rise` / `cut` — the two rows: Adjustment (signed, U+2212) and New total.
 * 💷 New total = the line's CURRENT total (`line_total + adjustment_total`, the
 * server's base) + this adjustment — never the original booked price when an
 * earlier adjustment exists. It is exactly what the request settles the hire at.
 * NO "recorded against your name" anywhere (owner: *"dont need Recorded against
 * your name."*).
 */
export function describeChoiceSummary(item, returnDate, state, formatMoney) {
  const NONE = { kind: 'none', text: 'No change — the booked price stands.' };
  const choice = state && state.choice;
  let adjustment;
  let base;
  if (choice === PRICE_CHOICE_SET) {
    const p = describeFinalPricePreview(item, state.price, formatMoney);
    if (p.final === null) return { kind: 'prompt', text: 'Enter the price this return settles at.' };
    if (p.offset === null) {
      return { kind: 'unknown', text: `Final price ${formatMoney(p.final)} — the server works out the adjustment from the agreed price.` };
    }
    adjustment = p.offset;
    base = p.currentTotal;
  } else if (choice === PRICE_CHOICE_ACTUAL) {
    const a = describeActualOption(item, returnDate, formatMoney);
    if (!a.available) return { kind: 'unknown', text: a.reason };
    // 6B: removing the earlier adjustment moves the total by its negation too.
    adjustment = removesPrior(a, state) ? roundMoney(a.delta - a.prior) : a.delta;
    base = describeFinalPricePreview(item, '', formatMoney).currentTotal;
  } else {
    return NONE;
  }
  if (adjustment === 0) return NONE;
  return {
    kind: adjustment > 0 ? 'rise' : 'cut',
    adjustment,
    adjustmentText: signedMoney(adjustment, formatMoney),
    newTotal: roundMoney(base + adjustment),
  };
}

const FIELD = 'w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100';
// While a note is owed the box stays RED, focused or not (owner 2026-10-19).
const FIELD_NOTE_OWED = 'w-full px-3 py-2 border-2 rounded-lg text-sm bg-white dark:bg-gray-700 border-red-500 dark:border-red-400 text-gray-900 dark:text-gray-100 focus:border-red-500 focus:ring-2 focus:ring-red-400 focus:outline-none';
const RADIO = 'h-4 w-4 shrink-0 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 disabled:opacity-50';
// The Set-final-price box ON its row: a fixed width in the price column.
const PRICE_BOX = 'w-28 px-2 py-1 border rounded-md text-sm text-right tabular-nums bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-500 disabled:cursor-not-allowed';
// Signed amounts: a rise is red, a cut green — the sign is the non-colour channel.
// 🪤 Why the figure also carries `data-money-tone` (the modal's dark
// `!important` table beats `dark:`) → `client/src/components/hires/CLAUDE.md`.
const SIGN_TONE = { rise: 'text-red-600 dark:text-red-400', cut: 'text-green-700 dark:text-green-400' };
// The summary's two figures sit in the SAME column as the option prices: the
// fieldset's 1px border + `p-1` + each row's `px-3` = 17px from the right edge.
const SUMMARY_GRID = 'grid grid-cols-[1fr_auto] items-baseline gap-x-3 gap-y-0.5 border-t border-gray-200 dark:border-gray-700 pt-2 px-[17px]';
const SUMMARY_LINE = 'text-sm text-gray-500 dark:text-gray-400';

/**
 * One option row: radio, name + a grey span ("1 week booked"), then the price
 * column (right-aligned, tabular — ONE column for every row). The whole row is
 * the label, so the hit area is the row; arrow keys move within the group
 * (native radios sharing a `name`). Selected = tinted AND checked.
 */
function ChoiceRow({ name, value, checked, disabled, onSelect, label, span, price, testId, rowTestId, children }) {
  return (
    <div data-testid={rowTestId}>
      <label
        className={`grid grid-cols-[auto_1fr_auto] items-center gap-x-2.5 px-3 py-2 rounded-md text-sm ${
          disabled ? 'cursor-not-allowed text-gray-400 dark:text-gray-500' : 'cursor-pointer text-gray-800 dark:text-gray-200'
        } ${checked ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'}`}
      >
        <input
          type="radio"
          name={name}
          value={value}
          checked={checked}
          disabled={disabled}
          onChange={() => onSelect(value)}
          className={RADIO}
          data-testid={testId}
        />
        <span>
          <span className={checked ? 'font-medium' : ''}>{label}</span>
          {span && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{span}</span>}
        </span>
        <span className="justify-self-end text-right tabular-nums">{price}</span>
      </label>
      {children}
    </div>
  );
}

/**
 * The summary under the options (R4, owner 2026-10-20) — `describeChoiceSummary`
 * decides, this only draws: ONE muted line, or the Adjustment / New total rows.
 */
function ChoiceSummary({ summary, tid, formatMoney }) {
  if (summary.kind !== 'rise' && summary.kind !== 'cut') {
    return <p className={SUMMARY_LINE} data-testid={tid(`price-summary-${summary.kind}`)}>{summary.text}</p>;
  }
  return (
    <div className={SUMMARY_GRID} data-testid={tid('price-summary')}>
      <span className="text-[13px] text-gray-500 dark:text-gray-400">Adjustment</span>
      <span
        className={`justify-self-end text-right tabular-nums text-sm font-medium ${SIGN_TONE[summary.kind]}`}
        data-money-tone={summary.kind}
        data-testid={tid('price-adjustment')}
      >
        {summary.adjustmentText}
      </span>
      <span className="text-sm font-medium text-gray-800 dark:text-gray-100">New total</span>
      <span className="justify-self-end text-right tabular-nums text-sm font-semibold text-gray-900 dark:text-gray-100" data-testid={tid('price-new-total')}>
        {formatMoney(summary.newTotal)}
      </span>
    </div>
  );
}

/**
 * The per-line price choice. CONTROLLED: `state` is `{ choice, price, touched,
 * note, error, nudge }` owned by the dialog (which also owns the return date
 * and runs `priceChoiceAfterDateChange` when it moves); `onChange(patch)`
 * merges. `tid(name)` maps a part name to that dialog's `data-testid`.
 *
 * ⚖️ 2026-10-20 (owner, as mocked): ALWAYS "Keep booked price" · "Charge actual
 * hire" · "Set final price", early, on time and late alike, with the price box
 * ON its row (disabled until chosen). "Charge actual hire" is HIDDEN — nothing
 * in its place — when the actual equals the booked price. ONE summary below
 * (`ChoiceSummary`). The note box goes RED while a note is owed, and a bumped
 * `state.nudge` shakes + flashes + focuses it.
 */
function OffHirePriceChoice({ item, returnDate, state, onChange, tid, formatMoney }) {
  const groupName = useId();
  const noteRef = useRef(null);
  const [flash, setFlash] = useState(false);
  const nudge = state.nudge || 0;
  useEffect(() => {
    if (!nudge) return undefined;
    setFlash(true);
    if (noteRef.current) noteRef.current.focus();
    const t = setTimeout(() => setFlash(false), 600);
    return () => clearTimeout(t);
  }, [nudge]);

  const actual = describeActualOption(item, returnDate, formatMoney);
  const prior = toMoneyNumber(item.adjustment_total);
  const summary = describeChoiceSummary(item, returnDate, state, formatMoney);
  const noteOwed = finalPriceNoteOwed(item, state, formatMoney);
  // R2 (owner 2026-10-20): actual == booked → the row is not offered at all.
  const showActualRow = !(actual.available && actual.matches);
  // 6B: the keep/remove question shows only while "Charge actual hire" is in force.
  const showPriorDecision = state.choice === PRICE_CHOICE_ACTUAL && actual.available
    && !actual.matches && actual.asksPriorDecision;
  const isSet = state.choice === PRICE_CHOICE_SET;

  const choose = (choice) => {
    // The note is per CHOICE: a Set-final-price note must never be appended
    // to the Charge-actual auto note, so a change of choice clears it.
    const note = choice === state.choice ? state.note : '';
    // 6B: leaving "Charge actual hire" forgets a Remove, so coming back starts at KEEP.
    const priorAdjustment = choice === state.choice ? state.priorAdjustment : PRIOR_ADJUSTMENT_KEEP;
    if (choice === PRICE_CHOICE_SET) {
      // Choosing it (again) prefills afresh for the date and follows it until
      // the user types — the 2026-09-28 prefill and follow rules.
      onChange({ choice, note, priorAdjustment, price: prefillFinalPrice(item, returnDate, formatMoney), touched: false, error: '' });
    } else {
      onChange({ choice, note, priorAdjustment, error: '' });
    }
  };

  return (
    <div className="space-y-2" data-testid={tid('price-choice')}>
      <fieldset className="border rounded-lg border-gray-200 dark:border-gray-700 p-1 space-y-0.5">
        <legend className="sr-only">Price for this return</legend>
        <ChoiceRow
          name={groupName}
          value={PRICE_CHOICE_KEEP}
          checked={state.choice === PRICE_CHOICE_KEEP}
          onSelect={choose}
          label="Keep booked price"
          span={actual.originalSpan ? `${actual.originalSpan} booked` : null}
          price={actual.original === null ? EM_DASH : formatMoney(actual.original)}
          testId={tid('price-keep')}
          rowTestId={tid('price-keep-row')}
        />
        {showActualRow && (
          <ChoiceRow
            name={groupName}
            value={PRICE_CHOICE_ACTUAL}
            checked={state.choice === PRICE_CHOICE_ACTUAL}
            disabled={!actual.available}
            onSelect={choose}
            label="Charge actual hire"
            span={actual.available && actual.actualSpan ? `${actual.actualSpan} used` : null}
            price={actual.available ? formatMoney(actual.actual) : EM_DASH}
            testId={tid('price-actual')}
            rowTestId={tid('price-actual-row')}
          >
            {!actual.available && (
              <p className="px-3 pb-2 pl-9 text-xs text-gray-500 dark:text-gray-400" data-testid={tid('price-actual-unavailable')}>
                {actual.reason}
              </p>
            )}
            {actual.available && actual.storedDiffers && (
              <p className="px-3 pb-2 pl-9 text-xs text-gray-500 dark:text-gray-400" data-testid={tid('price-actual-stored-differs')}>
                Stored price differs from rate × days — the difference is the cost of the {actual.delta > 0 ? 'extra' : 'fewer'} days.
              </p>
            )}
            {showPriorDecision && (
              // 6B: a nested group, indented under the "Charge actual hire" label
              // and shown only once it is chosen. Same words as Shorten's dialog.
              <fieldset className="ml-9 mr-3 mb-2 pl-3 border-l-2 border-amber-400 dark:border-amber-500/70" data-testid={tid('prior-decision')}>
                <legend className="text-xs font-medium text-amber-800 dark:text-amber-300">{ADJUSTMENT_EXISTS_TITLE}</legend>
                <div className="flex gap-4 mt-1 text-sm text-gray-800 dark:text-gray-200">
                  {[[PRIOR_ADJUSTMENT_KEEP, ADJUSTMENT_KEEP_LABEL], [PRIOR_ADJUSTMENT_REMOVE, ADJUSTMENT_REMOVE_LABEL]].map(([value, label]) => (
                    <label key={value} className="inline-flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name={`${groupName}-prior`}
                        value={value}
                        checked={(state.priorAdjustment || PRIOR_ADJUSTMENT_KEEP) === value}
                        onChange={() => onChange({ priorAdjustment: value, error: '' })}
                        className={RADIO}
                        data-testid={tid(`prior-${value}`)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-testid={tid('prior-remove-copy')}>
                  {adjustmentRemoveCopy(actual.prior, formatMoney)}
                </p>
              </fieldset>
            )}
          </ChoiceRow>
        )}
        <ChoiceRow
          name={groupName}
          value={PRICE_CHOICE_SET}
          checked={isSet}
          onSelect={choose}
          label="Set final price"
          price={
            // 💷 SHARED `ui/CurrencyInput`, ON the row (owner 2026-10-19), with
            // its own `£` glyph (`prefix` — exactly ONE thing renders the
            // symbol). DISABLED until this option is chosen. What is typed here
            // decides the money on an ADJUSTMENT ROW; a final PRICE is never
            // negative, so no `allowNegative` (the server refuses it too).
            <span className="inline-block w-28" onClick={(e) => { if (!isSet) e.preventDefault(); }}>
              <CurrencyInput
                value={isSet ? state.price : ''}
                onChange={(v) => onChange({ price: v, touched: finalPriceTouchedAfterInput(v), error: '' })}
                className={PRICE_BOX}
                prefix
                disabled={!isSet}
                testId={tid('amount')}
              />
            </span>
          }
          testId={tid('price-set')}
          rowTestId={tid('price-set-row')}
        />
      </fieldset>

      {state.choice === PRICE_CHOICE_ACTUAL && actual.available && !actual.matches && (
        // R3 (owner 2026-10-20): no "Recorded against your name …" line — the
        // auto note is still SENT and stored exactly as before (the audit record).
        <div className="space-y-1">
          <label className={FIELD_LABEL_CLASS} data-testid={tid('note-label')}>Note (optional)</label>
          <textarea
            value={state.note}
            onChange={(e) => onChange({ note: e.target.value, error: '' })}
            rows={2}
            className={FIELD}
            data-testid={tid('note')}
            placeholder="e.g. supplier confirmed the late return"
          />
        </div>
      )}

      {isSet && (
        <div>
          <label
            className={noteOwed ? 'block text-xs font-medium text-red-600 dark:text-red-400 mb-1' : FIELD_LABEL_CLASS}
            data-testid={tid('note-label')}
          >
            Note — required if the price changes
          </label>
          <textarea
            ref={noteRef}
            value={state.note}
            onChange={(e) => onChange({ note: e.target.value, error: '' })}
            rows={2}
            // Shake respects reduced motion (`motion-safe:`); the flash ring is
            // the non-motion half of the nudge.
            className={`${noteOwed ? FIELD_NOTE_OWED : FIELD}${flash ? ' motion-safe:animate-shake ring-4 ring-red-300 dark:ring-red-500/60' : ''}`}
            aria-invalid={noteOwed ? 'true' : undefined}
            data-testid={tid('note')}
            placeholder="e.g. supplier invoiced a restocking fee"
          />
        </div>
      )}

      {prior !== null && prior !== 0 && !showPriorDecision && (
        // Under "Charge actual hire" the keep/remove question above says this instead.
        <p className="text-xs text-gray-500 dark:text-gray-400" data-testid={tid('price-prior')}>
          Earlier adjustments of {formatMoney(prior)} stay on this hire unless you set a final price{actual.available && !actual.matches ? ' or remove them under Charge actual hire' : ''}.
        </p>
      )}

      <ChoiceSummary summary={summary} tid={tid} formatMoney={formatMoney} />

      {state.error && (
        <p className="text-xs text-red-600 dark:text-red-400" data-testid={tid('error')}>{state.error}</p>
      )}
    </div>
  );
}

export default OffHirePriceChoice;
